
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "@/hooks/useProject";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "lucide-react";

interface DuplicateTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: any;
}

export function DuplicateTemplateDialog({ 
  open, 
  onOpenChange, 
  template 
}: DuplicateTemplateDialogProps) {
  const [newName, setNewName] = useState(`${template.name || "Sin nombre"} (Copia)`);
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const { projectClient, currentProject } = useProject();

  const handleDuplicate = async () => {
    if (!newName.trim()) {
      toast.error("El nombre no puede estar vacío");
      return;
    }

    try {
      setIsLoading(true);

      // Track duplication statistics
      const stats = {
        transformations: 0,
        variables: 0,
        charts: 0,
        calculations: 0,
        images: 0,
        variableMappings: 0
      };

      // Get the original template data
      const { data: originalTemplate, error: fetchError } = await projectClient
        .from("report_templates_v2")
        .select("*")
        .eq("id", template.id)
        .eq('project_id', projectClient.getProjectId())
        .single();

      if (fetchError) throw fetchError;

      // Get transformations if they exist
      const { data: transformations, error: transformationsError } = await projectClient
        .from("report_template_transformations_v2")
        .select("*")
        .eq("template_id", template.id)
        .eq('project_id', projectClient.getProjectId());

      if (transformationsError) throw transformationsError;

      // Create new template
      const { data: newTemplate, error: insertError } = await projectClient
        .from("report_templates_v2")
        .insert({
          name: newName,
          template: originalTemplate.template,
          documentation: originalTemplate.documentation,
          ppt_file_path: originalTemplate.ppt_file_path,
          project_id: projectClient.getProjectId()
        })
        .select("*")
        .single();

      if (insertError) throw insertError;

      // Copy transformations if they exist
      if (transformations && transformations.length > 0) {
        const newTransformations = transformations.map(t => ({
          var_name: t.var_name,
          transformation: t.transformation,
          template_id: newTemplate.id,
          project_id: projectClient.getProjectId()
        }));

        const { error: transformInsertError } = await projectClient
          .from("report_template_transformations_v2")
          .insert(newTransformations);

        if (transformInsertError) throw transformInsertError;
        stats.transformations = newTransformations.length;
      }

      // Duplicate template_variables_v2
      try {
        const { data: variables, error: variablesError } = await projectClient
          .from("template_variables_v2")
          .select("*")
          .eq("template_id", template.id)
          .eq('project_id', projectClient.getProjectId());

        if (!variablesError && variables && variables.length > 0) {
          const newVariables = variables.map(v => ({
            variable_name: v.variable_name,
            variable_type: v.variable_type,
            description: v.description,
            is_custom: v.is_custom,
            template_id: newTemplate.id,
            project_id: projectClient.getProjectId()
          }));

          const { error: varsInsertError } = await projectClient
            .from("template_variables_v2")
            .insert(newVariables);

          if (!varsInsertError) {
            stats.variables = newVariables.length;
          } else {
            console.error("Error duplicando variables:", varsInsertError);
            toast.warning("No se pudieron duplicar todas las variables");
          }
        }
      } catch (error) {
        console.error("Error en duplicación de variables:", error);
      }

      // Duplicate template_charts
      try {
        const { data: charts, error: chartsError } = await projectClient
          .from("template_charts")
          .select("*")
          .eq("template_id", template.id)
          .eq('project_id', projectClient.getProjectId());

        if (!chartsError && charts && charts.length > 0) {
          const newCharts = charts.map(c => ({
            name: c.name,
            description: c.description,
            slide_number: c.slide_number,
            position_x: c.position_x,
            position_y: c.position_y,
            width: c.width,
            height: c.height,
            chart_type: c.chart_type,
            chart_config: c.chart_config,
            preview_url: c.preview_url,
            preview_updated_at: c.preview_updated_at,
            display_order: c.display_order,
            is_active: c.is_active,
            template_id: newTemplate.id,
            project_id: projectClient.getProjectId()
          }));

          const { error: chartsInsertError } = await projectClient
            .from("template_charts")
            .insert(newCharts);

          if (!chartsInsertError) {
            stats.charts = newCharts.length;
          } else {
            console.error("Error duplicando gráficos:", chartsInsertError);
            toast.warning("No se pudieron duplicar todos los gráficos");
          }
        }
      } catch (error) {
        console.error("Error en duplicación de gráficos:", error);
      }

      // Duplicate template_calculations
      try {
        const { data: calculations, error: calculationsError } = await projectClient
          .from("template_calculations")
          .select("*")
          .eq("template_id", template.id)
          .eq('project_id', projectClient.getProjectId());

        if (!calculationsError && calculations && calculations.length > 0) {
          const newCalculations = calculations.map(c => ({
            name: c.name,
            calculation_type: c.calculation_type,
            config: c.config,
            template_id: newTemplate.id,
            project_id: projectClient.getProjectId()
          }));

          const { error: calcsInsertError } = await projectClient
            .from("template_calculations")
            .insert(newCalculations);

          if (!calcsInsertError) {
            stats.calculations = newCalculations.length;
          } else {
            console.error("Error duplicando cálculos:", calcsInsertError);
            toast.warning("No se pudieron duplicar todos los cálculos");
          }
        }
      } catch (error) {
        console.error("Error en duplicación de cálculos:", error);
      }

      // Duplicate template_images
      try {
        const { data: images, error: imagesError } = await projectClient
          .from("template_images")
          .select("*")
          .eq("template_id", template.id)
          .eq('project_id', projectClient.getProjectId());

        if (!imagesError && images && images.length > 0) {
          const newImages = images.map(i => ({
            name: i.name,
            description: i.description,
            slide_number: i.slide_number,
            position_x: i.position_x,
            position_y: i.position_y,
            width: i.width,
            height: i.height,
            image_type: i.image_type,
            image_config: i.image_config,
            fit_mode: i.fit_mode,
            display_order: i.display_order,
            is_active: i.is_active,
            template_id: newTemplate.id,
            project_id: projectClient.getProjectId()
          }));

          const { error: imagesInsertError } = await projectClient
            .from("template_images")
            .insert(newImages);

          if (!imagesInsertError) {
            stats.images = newImages.length;
          } else {
            console.error("Error duplicando imágenes:", imagesInsertError);
            toast.warning("No se pudieron duplicar todas las imágenes");
          }
        }
      } catch (error) {
        console.error("Error en duplicación de imágenes:", error);
      }

      // Duplicate template_variable_mappings
      try {
        const { data: mappings, error: mappingsError } = await projectClient
          .from("template_variable_mappings")
          .select("*")
          .eq("template_id", template.id)
          .eq('project_id', projectClient.getProjectId());

        if (!mappingsError && mappings && mappings.length > 0) {
          const newMappings = mappings.map(m => ({
            variable_name: m.variable_name,
            variable_placeholder: m.variable_placeholder,
            source_type: m.source_type,
            question_id: m.question_id,
            question_name: m.question_name,
            question_group: m.question_group,
            form_source: m.form_source,
            calculation_type: m.calculation_type,
            calculation_config: m.calculation_config,
            description: m.description,
            default_value: m.default_value,
            matrix_row_id: m.matrix_row_id,
            matrix_row_text: m.matrix_row_text,
            template_id: newTemplate.id,
            project_id: projectClient.getProjectId()
          }));

          const { error: mappingsInsertError } = await projectClient
            .from("template_variable_mappings")
            .insert(newMappings);

          if (!mappingsInsertError) {
            stats.variableMappings = newMappings.length;
          } else {
            console.error("Error duplicando mapeos de variables:", mappingsInsertError);
            toast.warning("No se pudieron duplicar todos los mapeos de variables");
          }
        }
      } catch (error) {
        console.error("Error en duplicación de mapeos:", error);
      }

      // Duplicate PPT file if it exists
      if (originalTemplate.ppt_file_path) {
        try {
          const { data: fileData, error: downloadError } = await supabase.storage
            .from('templates')
            .download(originalTemplate.ppt_file_path);

          if (downloadError) throw downloadError;

          const newFileName = `template_${newTemplate.id}_${Date.now()}.${originalTemplate.ppt_file_path.split('.').pop()}`;
          const newFilePath = `template_files/${newFileName}`;

          const { error: uploadError } = await supabase.storage
            .from('templates')
            .upload(newFilePath, fileData);

          if (uploadError) throw uploadError;

          // Update the new template with the new file path
          const { error: updateError } = await projectClient
            .from("report_templates_v2")
            .update({ ppt_file_path: newFilePath })
            .eq("id", newTemplate.id)
            .eq('project_id', projectClient.getProjectId());

          if (updateError) throw updateError;
        } catch (fileError: any) {
          console.error("Error duplicando archivo:", fileError);
          // Continue even if file duplication fails
          toast.warning("La plantilla se duplicó pero hubo un error al duplicar el archivo PPT");
        }
      }

      // Build success message with statistics
      const duplicatedItems = [];
      if (stats.transformations > 0) duplicatedItems.push(`${stats.transformations} transformación(es)`);
      if (stats.variables > 0) duplicatedItems.push(`${stats.variables} variable(s)`);
      if (stats.charts > 0) duplicatedItems.push(`${stats.charts} gráfico(s)`);
      if (stats.calculations > 0) duplicatedItems.push(`${stats.calculations} cálculo(s)`);
      if (stats.images > 0) duplicatedItems.push(`${stats.images} imagen(es)`);
      if (stats.variableMappings > 0) duplicatedItems.push(`${stats.variableMappings} mapeo(s) de variables`);

      const successMessage = duplicatedItems.length > 0
        ? `Plantilla duplicada con: ${duplicatedItems.join(', ')}`
        : "Plantilla duplicada correctamente";

      toast.success(successMessage);
      queryClient.invalidateQueries({ queryKey: ["templates_v2", currentProject] });
      onOpenChange(false);
    } catch (error: any) {
      toast.error(`Error al duplicar la plantilla: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Duplicar plantilla</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre de la nueva plantilla</Label>
            <Input
              id="name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ingrese un nombre para la plantilla"
            />
          </div>
        </div>
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDuplicate}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                Duplicando...
              </>
            ) : (
              'Duplicar'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
